import { Product } from '@/programmes/components/Products/models'
import {
  DiscountDetails,
  ProductGroups,
} from '@/programmes/WeightLoss/CheckOut/ProductPicker/models/models'
import {
  LITE_PRODUCT_TYPE,
  MOUNJARO_PRODUCT_ID,
  ULTRA_PRODUCT_TYPE,
  WEGOVY_PRODUCT_ID,
} from '@/programmes/WeightLoss/CheckOut/ProductPicker/data/const'

const getDiscountValues = (
  discount: DiscountDetails,
  price: number
): { discount: string; priceWithDiscount: string } => {
  if (price === 0) {
    return {
      discount: '',
      priceWithDiscount: '',
    }
  }

  const discountValue = discount.discountPercentage
    ? price * (discount.discountPercentage / 100)
    : discount.discountAmount / 100

  const priceWithDiscount = Math.max(0, price - discountValue)

  return {
    discount: discountValue > 0 ? `£${discountValue.toFixed(2)}` : '',
    priceWithDiscount: `£${priceWithDiscount.toFixed(2)}`,
  }
}

const updateProduct = (
  productName: string,
  productType: string,
  stripeProducts: Product[],
  productGroups: ProductGroups,
  discountDetails: DiscountDetails,
  addons: Product[]
) => {
  // Check if Stripe data contains products that can be used to enhance product picker data.
  const products = stripeProducts.filter((product) => {
    const productWords = product.name.toLowerCase().split(' ')

    return (
      productWords.includes(productName) && productWords.includes(productType)
    )
  })

  // Return hardcoded data if there is more than one product.
  if (products.length > 1) {
    return productGroups
  }

  // Return hardcoded data if there is not even one product.
  if (products.length === 0) {
    return productGroups
  }

  const product = products[0]

  const price = product.prices.find(
    (price) => price.id === product.defaultPrice
  )

  // Return hardcoded data if the product does not contain the default price.
  if (!price) {
    return productGroups
  }

  // Add price details
  productGroups[productName].products[productType].price = `£${(
    price.unitAmount / 100
  ).toFixed(2)}`
  productGroups[productName].products[productType].id = price.lookupKey
  productGroups[productName].products[productType].name = product.name
  productGroups[productName].icon = product.images[0]

  // Add discount details if available.
  if (
    discountDetails &&
    discountDetails.prices.some(
      (price) =>
        price.priceLookupKey ===
          productGroups[productName].products[productType].id &&
        price.isApplicable === true
    )
  ) {
    const { discount, priceWithDiscount } = getDiscountValues(
      discountDetails,
      price.unitAmount / 100
    )

    productGroups[productName].products[productType].discount = discount
    productGroups[productName].products[
      productType
    ].priceWithDiscount = priceWithDiscount
  }

  // Add add-ons to ultra products if add-ons are available.
  if (productType === ULTRA_PRODUCT_TYPE) {
    const mappedAddons = addons.map((addon) => {
      const addonPrice = addon.prices.find(
        (price) => price.id === addon.defaultPrice
      )

      return addonPrice?.lookupKey
    })

    productGroups[productName].products[productType].addons = mappedAddons
  }

  return productGroups
}

export const addStripeLiveData = (
  stripeProducts: Product[],
  productGroups: ProductGroups,
  discountDetails: DiscountDetails,
  addons: Product[]
) => {
  let enhancedProductGroups = { ...productGroups }

  ;[WEGOVY_PRODUCT_ID, MOUNJARO_PRODUCT_ID].forEach((productName) => {
    ;[ULTRA_PRODUCT_TYPE, LITE_PRODUCT_TYPE].forEach((productType) => {
      enhancedProductGroups = updateProduct(
        productName,
        productType,
        stripeProducts,
        enhancedProductGroups,
        discountDetails,
        addons
      )
    })
  })

  return enhancedProductGroups
}
